/**
 * SubscriptionService — single source of truth for all per-plan limits.
 *
 * This file consolidates two previously separate concerns:
 *   1) Tajir AI quotas (was TajirPlanService.ts)
 *   2) Dashboard customization rules (was scattered in DashboardManager + ad-hoc)
 *
 * Every other module that needs to know "what can plan X do" should import
 * from here, not duplicate the policy.
 */

import { TajirPlan, TajirPlanPolicy } from '../types/tajir';
import { TAJIR_PLAN_POLICIES } from './TajirPlanService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type SubscriptionTier = TajirPlan; // 'free' | 'light' | 'base' | 'advanced'

/** Tier of a single widget, controlling who can drop it on a dashboard. */
export type WidgetTier = 'free' | 'base';

export interface DashboardTierPolicy {
  /** Hard cap on total dashboards (default + user-created). Infinity for advanced. */
  maxDashboards: number;
  /** User can create new dashboards (or clone existing ones). */
  canCreate: boolean;
  /** User can delete dashboards (including the defaults). */
  canDelete: boolean;
  /** User can rename dashboards. */
  canRename: boolean;
  /** User can add/remove/reorder widgets inside a dashboard. */
  canEditWidgets: boolean;
  /**
   * Standard dashboards this plan gets installed. `undefined` = all of them.
   * Used to keep Free on Overview + Tools only.
   */
  allowedPresetIds?: string[];
  /** Widget tiers this plan can drop on dashboards. */
  allowedWidgetTiers: WidgetTier[];
}

export interface ReportTierPolicy {
  /** Numero massimo di report AI standard generabili per mese. */
  monthlyReports: number;
  /**
   * Report avanzati (bias cognitivi + efficienza operativa con
   * counterfactuals tipo "se fosse uscito prima / no overtrading").
   * 0 = bloccato per il piano. Disponibile da Base in su.
   */
  monthlyAdvancedReports: number;
  /**
   * Se false, l'AI report cade in fallback offline (no API call).
   * Sempre true al momento, ma utile come switch d'emergenza.
   */
  aiEnabled: boolean;
}

export interface DiaryTierPolicy {
  /** Numero massimo di cartelle attive. Infinity = fair-use alto/illimitato lato prodotto. */
  maxFolders: number;
  /** Numero massimo di pagine attive. Infinity = illimitate per il piano. */
  maxPages: number;
  /** Riassunti AI generabili al mese. */
  monthlyAiSummaries: number;
  /** Immagini caricabili al mese. */
  monthlyImages: number;
  /** Accesso al comando /ai nel Diary. */
  canUseAiSummary: boolean;
  /** Accesso al caricamento immagini nel Diary. */
  canUploadImages: boolean;
}

export interface DataImportTierPolicy {
  /** Numero massimo di file trading attivi importabili. Infinity = fair-use/internal. */
  maxActiveFiles: number;
  /** Numero massimo di trade accettati per singolo file. Infinity = fair-use/internal. */
  maxTradesPerFile: number;
}

export interface SubscriptionUiMetadata {
  /** Evidenzia il piano nella UI pricing/billing. */
  recommended: boolean;
  /** Badge breve: es. "Consigliato". */
  popularLabel?: string;
  /** Copy da mostrare nella card abbonamento. */
  billingDescription: string;
  /** CTA primaria per la card piano. */
  ctaLabel: string;
  /** False finché Stripe Checkout/Portal non sono collegati davvero. */
  stripeReady: boolean;
}

export interface SubscriptionPolicy {
  tier: SubscriptionTier;
  /** Customer-facing label (used in UI badges). */
  label: string;
  /** Show this tier in pricing UI / tier selectors. Advanced is hidden for now. */
  visible: boolean;
  /** ~Monthly price in USD — the currency the Stripe Prices are in. 0 for free. */
  monthlyPrice: number;
  /** Single-line marketing pitch for upgrade CTAs. */
  shortPitch: string;
  /** Tajir AI quotas (re-exported from TajirPlanService for back-compat). */
  tajir: TajirPlanPolicy;
  /** Dashboard customization rules. */
  dashboards: DashboardTierPolicy;
  /** AI report quotas (DeepSeek-reasoner backed). */
  reports: ReportTierPolicy;
  /** Diary workspace limits and AI summary/image quotas. */
  diary: DiaryTierPolicy;
  /** Import file/trade limits for the trading data workspace. */
  dataImport: DataImportTierPolicy;
  /** Billing/settings presentation metadata. */
  ui: SubscriptionUiMetadata;
}

// ---------------------------------------------------------------------------
// Early-access discount
// ---------------------------------------------------------------------------
/**
 * The launch discount. The list price stays what it is and the reduction is a
 * discount rather than a lower price, for two reasons: it can be withdrawn
 * without repricing anything, and existing subscribers keep the rate they
 * signed up at because Stripe holds the coupon against the subscription.
 *
 * IMPORTANT: the amount is applied by Stripe, not by this file. The checkout
 * function refuses to open a session when the discount is advertised here but
 * `STRIPE_PROMO_CODE_BETA` is unset — a UI promising 50% off while Stripe
 * charges the full price is worse than no discount at all.
 *
 * That is the secret name to check: `stripe-checkout` reads
 * `STRIPE_PROMO_CODE_BETA` and nothing else. This comment used to name
 * `STRIPE_COUPON_BETA_50`, which is an older variable that still exists in the
 * project — so following it led to verifying a secret the code never reads.
 */
export const BETA_DISCOUNT = {
  active: true,
  percentOff: 50,
  /**
   * The code the customer types at checkout. Advertised on the pricing page and
   * verified by stripe-checkout against Stripe before a session is opened: if
   * this names a code the Stripe account does not have, the function refuses
   * with a 503 rather than quietly charging the full price the page struck out.
   *
   * Must match the promotion code stored in STRIPE_PROMO_CODE_BETA.
   */
  code: 'BETA26',
  /** The promotion code lives in this function secret. */
  promoCodeEnv: 'STRIPE_PROMO_CODE_BETA'
} as const;

/** List price with the early-access discount applied, rounded to cents. */
export const discountedPrice = (fullPrice: number): number => {
  if (!BETA_DISCOUNT.active || fullPrice <= 0) return fullPrice;
  return Math.round(fullPrice * (1 - BETA_DISCOUNT.percentOff / 100) * 100) / 100;
};

// ---------------------------------------------------------------------------
// Policies
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_POLICIES: Record<SubscriptionTier, SubscriptionPolicy> = {
  free: {
    tier: 'free',
    label: 'Freemium',
    visible: true,
    monthlyPrice: 0,
    shortPitch: 'Start free with 2 standard dashboards and Tajir AI in essential mode.',
    tajir: TAJIR_PLAN_POLICIES.free,
    dashboards: {
      maxDashboards: 2,
      canCreate: false,
      canDelete: false,
      canRename: false,
      canEditWidgets: false,
      allowedWidgetTiers: ['free'],
      // BETA: Free only gets these two standard dashboards installed.
      allowedPresetIds: ['overview-dashboard', 'tools-dashboard'],
    },
    reports: {
      monthlyReports: 1,
      monthlyAdvancedReports: 0,
      aiEnabled: true,
    },
    diary: {
      maxFolders: 1,
      maxPages: 2,
      monthlyAiSummaries: 3,
      monthlyImages: 0,
      canUseAiSummary: true,
      canUploadImages: false,
    },
    dataImport: {
      maxActiveFiles: 1,
      maxTradesPerFile: 50000,
    },
    ui: {
      recommended: false,
      billingDescription: 'Try TradingSpace with tight limits and no payment required.',
      ctaLabel: 'Current plan',
      stripeReady: false,
    },
  },
  light: {
    tier: 'light',
    label: 'Light',
    // BETA: only Free and Base are on sale. Light stays in the codebase so
    // existing/grandfathered accounts keep working, but it is not offered.
    visible: false,
    monthlyPrice: 8.9,
    shortPitch: 'Customize up to 3 dashboards and use Diary, reports and Tajir AI with roomier limits.',
    tajir: TAJIR_PLAN_POLICIES.light,
    dashboards: {
      maxDashboards: 3,
      canCreate: true,
      canDelete: true,
      canRename: true,
      canEditWidgets: true,
      allowedWidgetTiers: ['free'],
    },
    reports: {
      monthlyReports: 3,
      monthlyAdvancedReports: 0,
      aiEnabled: true,
    },
    diary: {
      maxFolders: 2,
      maxPages: Infinity,
      monthlyAiSummaries: 60,
      monthlyImages: 30,
      canUseAiSummary: true,
      canUploadImages: true,
    },
    dataImport: {
      maxActiveFiles: 2,
      maxTradesPerFile: 50000,
    },
    ui: {
      recommended: false,
      billingDescription: 'Get serious with editable dashboards, Diary and basic AI.',
      ctaLabel: 'Switch to Light',
      stripeReady: false,
    },
  },
  base: {
    tier: 'base',
    label: 'Base',
    visible: true,
    // BETA pricing: the single paid plan during the beta launch.
    monthlyPrice: 17.9,
    shortPitch: 'Unlock every advanced widget, up to 5 dashboards and Tajir AI with an extended budget.',
    tajir: TAJIR_PLAN_POLICIES.base,
    dashboards: {
      maxDashboards: 5,
      canCreate: true,
      canDelete: true,
      canRename: true,
      canEditWidgets: true,
      allowedWidgetTiers: ['free', 'base'],
    },
    reports: {
      monthlyReports: 3,
      monthlyAdvancedReports: 2,
      aiEnabled: true,
    },
    diary: {
      maxFolders: Infinity,
      maxPages: Infinity,
      monthlyAiSummaries: 300,
      monthlyImages: 500,
      canUseAiSummary: true,
      canUploadImages: true,
    },
    dataImport: {
      maxActiveFiles: 5,
      maxTradesPerFile: Infinity,
    },
    ui: {
      recommended: true,
      popularLabel: 'Recommended',
      billingDescription: 'The main plan for active traders: reports, Tajir AI and roomier data limits.',
      ctaLabel: 'Upgrade to Base',
      stripeReady: true,
    },
  },
  advanced: {
    tier: 'advanced',
    label: 'Advanced',
    visible: false, // Hidden in pricing UI for now
    monthlyPrice: 50,
    shortPitch: 'Unlimited dashboards, Tajir AI with no compromises, priority access to new features.',
    tajir: TAJIR_PLAN_POLICIES.advanced,
    dashboards: {
      maxDashboards: Infinity,
      canCreate: true,
      canDelete: true,
      canRename: true,
      canEditWidgets: true,
      allowedWidgetTiers: ['free', 'base'],
    },
    reports: {
      monthlyReports: 999,
      monthlyAdvancedReports: 999,
      aiEnabled: true,
    },
    diary: {
      maxFolders: Infinity,
      maxPages: Infinity,
      monthlyAiSummaries: 999,
      monthlyImages: 999,
      canUseAiSummary: true,
      canUploadImages: true,
    },
    dataImport: {
      maxActiveFiles: Infinity,
      maxTradesPerFile: Infinity,
    },
    ui: {
      recommended: false,
      popularLabel: 'Internal',
      billingDescription: 'Internal/admin plan for testing, support and selected power users.',
      ctaLabel: 'Internal management',
      stripeReady: false,
    },
  },
};

// ---------------------------------------------------------------------------
// Helpers — prefer using these to direct policy access in components
// ---------------------------------------------------------------------------

/** Get the full subscription policy for a plan (defaults to free if invalid). */
export function getSubscriptionPolicy(plan: SubscriptionTier | string | undefined | null): SubscriptionPolicy {
  if (plan && plan in SUBSCRIPTION_POLICIES) {
    return SUBSCRIPTION_POLICIES[plan as SubscriptionTier];
  }
  return SUBSCRIPTION_POLICIES.free;
}

/** Whether the user can create another dashboard given their current count. */
export function canCreateDashboard(plan: SubscriptionTier, currentCount: number): boolean {
  const p = getSubscriptionPolicy(plan).dashboards;
  if (!p.canCreate) return false;
  return currentCount < p.maxDashboards;
}

/** Whether the user can delete a specific dashboard. */
export function canDeleteDashboard(plan: SubscriptionTier): boolean {
  return getSubscriptionPolicy(plan).dashboards.canDelete;
}

/** Whether the user can rename a dashboard. */
export function canRenameDashboard(plan: SubscriptionTier): boolean {
  return getSubscriptionPolicy(plan).dashboards.canRename;
}

/** Whether the user can add/remove widgets on dashboards. */
export function canEditDashboardWidgets(plan: SubscriptionTier): boolean {
  return getSubscriptionPolicy(plan).dashboards.canEditWidgets;
}

/** Whether the user's plan can use a widget with the given required tier. */
export function canUseWidget(
  plan: SubscriptionTier,
  widget: { requiredPlan?: WidgetTier | string },
): boolean {
  const required = (widget.requiredPlan || 'free') as WidgetTier;
  const allowed = getSubscriptionPolicy(plan).dashboards.allowedWidgetTiers;
  return allowed.includes(required);
}

/** Inverse of `canUseWidget` — used to drive the "Base+" badge in the catalog. */
export function isWidgetLockedForPlan(
  plan: SubscriptionTier,
  widget: { requiredPlan?: WidgetTier | string },
): boolean {
  return !canUseWidget(plan, widget);
}

/** Suggested next tier for an upgrade CTA (returns null if already on top). */
export function getNextTier(plan: SubscriptionTier): SubscriptionTier | null {
  const ladder: SubscriptionTier[] = ['free', 'light', 'base', 'advanced'];
  const idx = ladder.indexOf(plan);
  if (idx < 0 || idx >= ladder.length - 1) return null;
  // Skip hidden tiers when suggesting next
  for (let i = idx + 1; i < ladder.length; i++) {
    if (SUBSCRIPTION_POLICIES[ladder[i]].visible) return ladder[i];
  }
  return null;
}

/** Tiers visible in pricing UI, in display order. */
export function getVisibleTiers(): SubscriptionPolicy[] {
  return Object.values(SUBSCRIPTION_POLICIES).filter((p) => p.visible);
}

/* ---------- Report quota helpers ---------- */

/** Whether the user can generate another standard AI report this month. */
export function canGenerateReport(plan: SubscriptionTier, currentCountThisMonth: number): boolean {
  const p = getSubscriptionPolicy(plan).reports;
  if (!p.aiEnabled) return false;
  return currentCountThisMonth < p.monthlyReports;
}

/** Whether the user can generate another advanced AI report this month. */
export function canGenerateAdvancedReport(
  plan: SubscriptionTier,
  currentCountThisMonth: number,
): boolean {
  const p = getSubscriptionPolicy(plan).reports;
  if (!p.aiEnabled) return false;
  if (p.monthlyAdvancedReports <= 0) return false;
  return currentCountThisMonth < p.monthlyAdvancedReports;
}

/** Returns the configured monthly limits for the plan. */
export function getReportLimit(plan: SubscriptionTier): { standard: number; advanced: number } {
  const p = getSubscriptionPolicy(plan).reports;
  return { standard: p.monthlyReports, advanced: p.monthlyAdvancedReports };
}

/* ---------- Diary helpers ---------- */

export function getDiaryPolicy(plan: SubscriptionTier | string | undefined | null): DiaryTierPolicy {
  return getSubscriptionPolicy(plan).diary;
}

export function canCreateDiaryPage(plan: SubscriptionTier, currentPages: number): boolean {
  const p = getSubscriptionPolicy(plan).diary;
  return currentPages < p.maxPages;
}

export function canCreateDiaryFolder(plan: SubscriptionTier, currentFolders: number): boolean {
  const p = getSubscriptionPolicy(plan).diary;
  return currentFolders < p.maxFolders;
}

export function canUseDiaryAiSummary(plan: SubscriptionTier, usedThisMonth: number): boolean {
  const p = getSubscriptionPolicy(plan).diary;
  if (!p.canUseAiSummary) return false;
  return usedThisMonth < p.monthlyAiSummaries;
}

export function canUploadDiaryImage(plan: SubscriptionTier, usedThisMonth: number): boolean {
  const p = getSubscriptionPolicy(plan).diary;
  if (!p.canUploadImages) return false;
  return usedThisMonth < p.monthlyImages;
}

/* ---------- Data import helpers ---------- */

export function getDataImportPolicy(plan: SubscriptionTier | string | undefined | null): DataImportTierPolicy {
  return getSubscriptionPolicy(plan).dataImport;
}

export function canImportNewTradingFile(plan: SubscriptionTier, currentRealFiles: number): boolean {
  const p = getSubscriptionPolicy(plan).dataImport;
  return currentRealFiles < p.maxActiveFiles;
}

export function canImportTradeCount(plan: SubscriptionTier, tradeCount: number): boolean {
  const p = getSubscriptionPolicy(plan).dataImport;
  return tradeCount <= p.maxTradesPerFile;
}
